/**
 * P0 Pilot Activation Guide — in-app markdown reader
 * ====================================================
 * Renders /app/memory/P0_PILOT_ACTIVATION_GUIDE.md inside the browser
 * via the admin-gated /api/pilot-activation/p0-guide endpoint.
 * Lives at /admin/p0-guide.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import SEOHead from '../../components/SEOHead';
import {
  ArrowLeft, BookOpen, Loader2, RefreshCw, ExternalLink, Rocket,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const P0Guide = () => {
  const [content, setContent] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchGuide = async () => {
    setLoading(true);
    setErr(null);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/pilot-activation/p0-guide`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContent(data.content);
      setMeta({ path: data.path, size_bytes: data.size_bytes, fetched_at: data.fetched_at });
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load the guide (admin required).');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGuide(); }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEOHead
        title="P0 Pilot Activation Guide · CARTIEAI"
        description="Step-by-step instructions to lift pilot-activation readiness from 58% to 100%."
        noindex
      />

      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="mb-6">
          <Link to="/admin/pilot-activation" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white" data-testid="p0-guide-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to Pilot Activation
          </Link>
        </div>

        <div className="mb-8">
          <Badge className="bg-emerald-500/20 text-emerald-200 mb-3">Admin · P0 onboarding</Badge>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2" data-testid="p0-guide-title">
            <BookOpen className="w-7 h-7 inline-block mr-2 text-emerald-400" />
            P0 Pilot Activation Guide
          </h1>
          <p className="text-sm sm:text-base text-slate-700">
            The 5 steps to lift readiness from <b>58%</b> to <b>100%</b>.{' '}
            <span className="text-slate-600">~15 minutes total · all 3 external keys have free tiers.</span>
          </p>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Button onClick={fetchGuide} disabled={loading} variant="outline" size="sm" data-testid="p0-guide-refresh">
              <RefreshCw className="w-3 h-3 mr-1.5" /> Reload from disk
            </Button>
            <Link to="/admin/pilot-activation" className="text-xs text-emerald-300 hover:underline inline-flex items-center gap-1">
              <Rocket className="w-3 h-3" /> Check current score →
            </Link>
            {meta && (
              <span className="text-[11px] text-slate-600 ml-auto">
                Source: <code>{meta.path}</code> · {meta.size_bytes} bytes
              </span>
            )}
          </div>
        </div>

        {loading && (
          <Card className="border-emerald-500/20">
            <CardContent className="pt-6 text-sm text-slate-600 flex items-center gap-2" data-testid="p0-guide-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Fetching guide from /app/memory/…
            </CardContent>
          </Card>
        )}

        {err && (
          <Card className="border-rose-500/30 bg-rose-500/10">
            <CardContent className="pt-6 text-sm text-rose-200" data-testid="p0-guide-error">
              {err}
            </CardContent>
          </Card>
        )}

        {content && (
          <Card className="border-emerald-500/20">
            <CardContent className="pt-6">
              <article
                className="prose prose-slate max-w-none prose-headings:font-semibold prose-h1:text-3xl prose-h2:text-xl prose-h2:mt-8 prose-h2:border-b prose-h2:border-emerald-500/20 prose-h2:pb-2 prose-code:text-emerald-300 prose-code:bg-slate-800 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-pre:bg-slate-900 prose-pre:text-slate-100 prose-a:text-emerald-300 prose-strong:text-white"
                data-testid="p0-guide-content"
              >
                <ReactMarkdown
                  components={{
                    a: ({ href, children, ...props }) => (
                      <a
                        href={href}
                        target={href?.startsWith('http') ? '_blank' : undefined}
                        rel={href?.startsWith('http') ? 'noreferrer' : undefined}
                        {...props}
                      >
                        {children}
                        {href?.startsWith('http') && <ExternalLink className="w-3 h-3 inline-block ml-0.5" />}
                      </a>
                    ),
                  }}
                >
                  {content}
                </ReactMarkdown>
              </article>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default P0Guide;
